import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import BudgetLineItemSection from './BudgetLineItemSection';
import { formatCalculatedNumber, normalizeLineItemNumerics } from '../hooks/useV3OfferBudget';
import { withoutClearedInheritingQuantities } from '../utils/budgetFieldValue';

const LINE_ITEMS_KEY = 'v3_budget_line_items';

// Mirrors V3OfferContext's sortByPosition: BudgetLineItemSection renders
// budget[lineItemsKey] in array order (it never sorts internally), and
// onReorderLineItems only stamps new `.position` values onto the existing
// array — the live app's per-category selectors (ticketLineItems, etc.) are
// what turn that into a visual reorder by re-sorting on read. This container
// uses one flat key for all 6 categories, so it has to do that same
// sort-on-read itself.
const sortByPosition = (a, b) => (a.position || 0) - (b.position || 0);

const SECTIONS = [
  { category: 'TICKET', title: 'Ticket tiers', addText: 'Add ticket tier', calculatedSectionKey: 'ticketsSection' },
  { category: 'ADDITION', title: 'Additions', addText: 'Add addition', calculatedSectionKey: 'additionsSection' },
  { category: 'INCOME', title: 'Ancillary Income', addText: 'Add income', calculatedSectionKey: 'revenueSection' },
  { category: 'BEFORE_ADJUSTED_DEDUCTIONS', title: 'Before Adjusted Deductions', addText: 'Add deduction', calculatedSectionKey: 'beforeAdjustedDeductionsSection' },
  { category: 'EXPENSES', title: 'Expenses', addText: 'Add expense', calculatedSectionKey: 'expensesSection' },
  { category: 'DEDUCTIONS', title: 'Deductions', addText: 'Add deduction', calculatedSectionKey: 'deductionsSection' },
];

const EditBudgetTemplateUI = ({ team, currentUser, csrfToken, api }) => {
  const { budget, createLineItem, updateLineItem, deleteLineItem, reorderLineItem, seedFor } = api;

  // BudgetLineItemSection drives its inputs off `budget`/`updateBudget`, calling
  // updateBudget on every keystroke to re-render with the in-progress value
  // before the debounced autosave lands, and calling onReorderLineItems with a
  // re-sorted copy after a drag. useV3BudgetTemplate only exposes a
  // network-backed updateBudget (PATCHes the template resource) and a
  // fire-and-forget reorderLineItem (no state update), so this container holds
  // its own copy and is the single source of truth for row order and in-progress
  // field values once loaded. It picks up api.budget only for the initial load
  // (edit/delete/reorder are reflected here already by the section's own
  // updateBudget/onReorderLineItems calls; add is reflected below once the
  // network call resolves) — never wholesale-overwritten afterward, so neither
  // a drag reorder nor an in-flight edit gets reverted by an unrelated
  // create/update/delete round-trip elsewhere on the page.
  const [localBudget, setLocalBudget] = useState(budget);
  const [collapsedSections, setCollapsedSections] = useState({});

  useEffect(() => {
    if (budget && !localBudget) {
      setLocalBudget(budget);
    }
  }, [budget, localBudget]);

  const allLineItems = localBudget ? (localBudget[LINE_ITEMS_KEY] || []) : [];

  // What each BudgetLineItemSection actually reads rows from — sorted by
  // position so a drag reorder (which only updates `.position` values, not
  // array order — see sortByPosition above) is visible on screen.
  const sortedBudget = useMemo(() => {
    if (!localBudget) return null;
    return { ...localBudget, [LINE_ITEMS_KEY]: [...allLineItems].sort(sortByPosition) };
  }, [localBudget, allLineItems]);

  if (!sortedBudget) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {SECTIONS.map((section, index) => {
        const isExpanded = !collapsedSections[section.category];
        return (
          <BudgetLineItemSection
            key={section.category}
            csrfToken={csrfToken}
            team={team}
            currentUser={currentUser}
            offer={null}
            budget={sortedBudget}
            calculated={{}}
            updateBudget={setLocalBudget}
            createBudgetLineItem={(_csrfToken, _team, _budget, position) =>
              createLineItem(seedFor(section.category, position)).then((created) => {
                if (!created) return;
                setLocalBudget((b) => ({
                  ...b,
                  [LINE_ITEMS_KEY]: [...(b[LINE_ITEMS_KEY] || []), created],
                }));
              })}
            deleteBudgetLineItem={(_csrfToken, _team, item) => deleteLineItem(item.id)}
            autoSaveBudgetLineItem={(_csrfToken, _team, item) => updateLineItem(item.id, normalizeLineItemNumerics(withoutClearedInheritingQuantities(item)))}
            showEditDetails={false}
            formatCalculatedNumber={formatCalculatedNumber}
            expanded={isExpanded}
            onToggleExpand={() => setCollapsedSections((prev) => ({
              ...prev,
              [section.category]: isExpanded,
            }))}
            sectionTitle={section.title}
            lineItemsKey={LINE_ITEMS_KEY}
            lineItemsFilter={(li) => li.category === section.category}
            calculatedSectionKey={section.calculatedSectionKey}
            addButtonText={section.addText}
            hasOfferColumn
            showOfferColumn
            showForecastColumn
            showActualsColumn={false}
            hideCollapsedColumns
            reorderLineItem={reorderLineItem}
            onReorderLineItems={(items) =>
              setLocalBudget((b) => ({ ...b, [LINE_ITEMS_KEY]: items }))}
            allLineItems={allLineItems}
            sectionTabIndexOffset={index * 1000}
          />
        );
      })}
    </div>
  );
};

EditBudgetTemplateUI.propTypes = {
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  api: PropTypes.object.isRequired,
};

export default EditBudgetTemplateUI;
